import axios from 'axios';
import { API_URL } from '../config/api';

const cache = new Map();
/** Refresh before S3 signature expiry */
const SAFETY_BUFFER_MS = 60_000;
const INTERNAL_FILE_PREFIXES = ['/api/files/', '/files/', '/uploads/'];
/** Keys we generate post-S3 are UUID + optional extension; anything else is legacy local. */
const UUID_KEY_RE = /^[0-9a-f-]{36}(\.[a-z0-9._-]{1,32})?$/i;

function isLikelyS3Key(key) {
  return typeof key === 'string' && UUID_KEY_RE.test(key);
}

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function decodeSafe(value) {
  try {
    return decodeURIComponent(value);
  } catch (_) {
    return value;
  }
}

function sanitizeOriginalName(name) {
  if (name == null || name === '') return '';
  const raw = Array.isArray(name)
    ? (name.map((part) => String(part ?? '').trim()).filter(Boolean).sort((a, b) => b.length - a.length)[0] || '')
    : String(name).trim();
  if (!raw.includes(',')) return raw;
  const dup = raw.match(/^([^,]+),\1(\.[^.,\\/]+)?$/);
  return dup ? `${dup[1]}${dup[2] || ''}` : raw;
}

export function extractInternalFileKey(fileKey) {
  if (!fileKey) return '';
  let value = decodeSafe(String(fileKey).trim());
  if (!value) return '';

  try {
    const parsed = new URL(value);
    value = parsed.pathname || value;
  } catch (_) {
    // not an absolute URL, continue
  }

  const withoutQuery = value.split('?')[0].split('#')[0];
  const normalized = withoutQuery.replace(/\\/g, '/');
  const lowered = normalized.toLowerCase();

  for (const prefix of INTERNAL_FILE_PREFIXES) {
    if (lowered.startsWith(prefix)) {
      return decodeSafe(normalized.slice(prefix.length)).replace(/^\/+/, '');
    }
  }

  return decodeSafe(normalized.replace(/^\/+/, ''));
}

function isAbsoluteExternalUrl(value) {
  if (!value) return false;
  try {
    const candidate = new URL(value);
    const apiOrigin = new URL(API_URL).origin;
    return candidate.origin !== apiOrigin;
  } catch (_) {
    return false;
  }
}

/**
 * Legacy URL through the API (local disk or S3 streaming). Used when S3 presign is unavailable.
 */
export function buildLegacyFileUrl(fileKey, options = {}) {
  if (!fileKey) return '';
  if (isAbsoluteExternalUrl(fileKey)) return fileKey;
  const normalizedKey = extractInternalFileKey(fileKey);
  if (!normalizedKey) return '';
  const params = new URLSearchParams();
  const includeToken = options.includeToken !== false;
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (includeToken && token) params.set('token', token);
  if (options.download) params.set('download', 'true');
  if (options.originalName) params.set('originalName', sanitizeOriginalName(options.originalName) || options.originalName);
  const q = params.toString();
  return `${API_URL}/files/${encodeURIComponent(normalizedKey)}${q ? `?${q}` : ''}`;
}

/**
 * Same-origin file URL for programmatic fetch (Authorization header).
 * Uses /api/files/... so CRA dev proxy or IIS routes to the backend.
 */
export function buildFileFetchUrl(fileKey, options = {}) {
  if (!fileKey) return '';
  if (isAbsoluteExternalUrl(fileKey)) return fileKey;
  const normalizedKey = extractInternalFileKey(fileKey);
  if (!normalizedKey) return '';
  const params = new URLSearchParams();
  if (options.download) params.set('download', 'true');
  if (options.originalName) params.set('originalName', sanitizeOriginalName(options.originalName) || options.originalName);
  const q = params.toString();
  return `/api/files/${encodeURIComponent(normalizedKey)}${q ? `?${q}` : ''}`;
}

/**
 * Returns a time-limited HTTPS URL (presigned) when S3 is configured, otherwise the legacy API URL.
 * Works for web and desktop (Electron) — both use this same resolver.
 */
export async function getPresignedFileUrl(fileKey, options = {}) {
  if (!fileKey) return null;
  if (isAbsoluteExternalUrl(fileKey)) return fileKey;
  const normalizedKey = extractInternalFileKey(fileKey);
  if (!normalizedKey) return null;
  if (!isLikelyS3Key(normalizedKey)) {
    return buildLegacyFileUrl(normalizedKey, options);
  }
  const originalName = sanitizeOriginalName(options.originalName);
  const cacheKey = `${normalizedKey}|${options.download ? '1' : '0'}|${originalName || ''}`;
  const cached = cache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now() + SAFETY_BUFFER_MS) {
    return cached.url;
  }
  try {
    const { data } = await axios.get(
      `${API_URL}/files/presign?key=${encodeURIComponent(normalizedKey)}`,
      {
        params: {
          ...(originalName ? { originalName } : {}),
          ...(options.download ? { download: true } : {}),
        },
        headers: authHeaders(),
      }
    );
    // Local-disk / S3-off: backend returns 200 with useLegacyUrl (no error noise in Network).
    if (data?.useLegacyUrl || data?.storage === 'local' || !data?.url) {
      return buildLegacyFileUrl(normalizedKey, {
        ...options,
        originalName: data?.originalName || originalName || options.originalName,
        download: data?.download === true ? true : options.download,
      });
    }
    if (data?.url) {
      const expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : Date.now() + 14 * 60 * 1000;
      cache.set(cacheKey, { url: data.url, expiresAt });
      return data.url;
    }
  } catch (e) {
    // Keep supporting older backends that returned 404/503 for local files.
    if (e.response?.status === 401 || e.response?.status === 403) {
      return buildLegacyFileUrl(normalizedKey, options);
    }
    if (e.response?.status === 400 || e.response?.status === 503 || e.response?.status === 404) {
      return buildLegacyFileUrl(normalizedKey, options);
    }
  }
  return buildLegacyFileUrl(normalizedKey, options);
}

export function invalidateFileUrlCache() {
  cache.clear();
}
